import { useEffect, useRef, useState } from 'react';

interface AutoFitTextProps {
  children: string;
  maxFont?: number;
  minFont?: number;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'div' | 'span';
  allowVerticalOverflow?: boolean;
  lineHeight?: number | string;
  verticalPaddingPx?: number;
  opticalCenter?: boolean;
  maxOpticalShiftPx?: number;
  opticalCenterWeight?: number;
  fontOpticalShiftY?: string;
}

const AutoFitText = ({
  children,
  maxFont = 48,
  minFont = 10,
  className = '',
  as: Component = 'div',
  allowVerticalOverflow = true,
  lineHeight = 1.25,
  verticalPaddingPx = 2,
  opticalCenter = false,
  maxOpticalShiftPx = 8,
  opticalCenterWeight = 1,
  fontOpticalShiftY = '0em',
}: AutoFitTextProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [fontSize, setFontSize] = useState<number>(maxFont);
  const [opticalShiftY, setOpticalShiftY] = useState<number>(0);

  const adjustFontSize = () => {
    if (!containerRef.current || !textRef.current) return;

    const container = containerRef.current;
    const text = textRef.current;

    // Reset a maxFont para medir desde cero
    text.style.fontSize = `${maxFont}px`;

    // Ajustar el font-size bajándolo de a 1px hasta que entre
    let currentSize = maxFont;
    while (currentSize >= minFont) {
      text.style.fontSize = `${currentSize}px`;

      // Forzar reflow para obtener medidas actualizadas
      void text.offsetWidth;

      if (text.scrollWidth <= container.clientWidth) {
        setFontSize(currentSize);
        return;
      }

      currentSize--;
    }

    // Si no entra ni al mínimo, establecer minFont
    text.style.fontSize = `${minFont}px`;
    setFontSize(minFont);
  };

  useEffect(() => {
    adjustFontSize();
    let cancelled = false;

    // ResizeObserver para recalcular cuando cambie el tamaño del contenedor
    const resizeObserver = new ResizeObserver(() => {
      adjustFontSize();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    if (document.fonts?.ready) {
      void document.fonts.ready.then(() => {
        if (!cancelled) {
          adjustFontSize();
        }
      });
    }

    // Limpiar el observer
    return () => {
      cancelled = true;
      resizeObserver.disconnect();
    };
  }, [children, maxFont, minFont]);

  useEffect(() => {
    if (!opticalCenter || !textRef.current) {
      setOpticalShiftY(0);
      return;
    }

    const measureOpticalShift = () => {
      if (!textRef.current) return;

      const text = textRef.current;
      const computed = window.getComputedStyle(text);

      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }

      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) {
        setOpticalShiftY(0);
        return;
      }

      const fontStyle = computed.fontStyle || 'normal';
      const fontWeight = computed.fontWeight || '400';
      const fontSizeComputed = computed.fontSize || `${fontSize}px`;
      const fontFamily = computed.fontFamily || 'sans-serif';

      // Keep canvas font shorthand conservative for broad browser support.
      ctx.font = `${fontStyle} ${fontWeight} ${fontSizeComputed} ${fontFamily}`;
      const metrics = ctx.measureText(children || '');

      const ascent = metrics.actualBoundingBoxAscent;
      const descent = metrics.actualBoundingBoxDescent;

      if (!Number.isFinite(ascent) || !Number.isFinite(descent) || (ascent === 0 && descent === 0)) {
        setOpticalShiftY(0);
        return;
      }

      // Positive value moves text down, which is needed for top-heavy handwritten fonts.
      const rawShift = ((ascent - descent) / 2) * opticalCenterWeight;
      const clampedShift = Math.max(-maxOpticalShiftPx, Math.min(maxOpticalShiftPx, rawShift));
      setOpticalShiftY(Math.round(clampedShift * 10) / 10);
    };

    measureOpticalShift();

    let cancelled = false;
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) {
          measureOpticalShift();
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, [children, fontSize, lineHeight, opticalCenter, maxOpticalShiftPx, opticalCenterWeight]);

  const containerProps = {
    ref: containerRef,
    className: className,
    style: {
      overflowX: 'hidden',
      overflowY: allowVerticalOverflow ? 'visible' : 'hidden',
      width: '100%',
      paddingTop: `${verticalPaddingPx}px`,
      paddingBottom: `${verticalPaddingPx}px`,
    },
  };

  const textProps = {
    ref: textRef as any,
    style: {
      display: 'inline-block',
      whiteSpace: 'nowrap',
      fontSize: `${fontSize}px`,
      lineHeight,
      transform: opticalCenter
        ? `translateY(calc(${opticalShiftY}px + ${fontOpticalShiftY}))`
        : (fontOpticalShiftY !== '0em' ? `translateY(${fontOpticalShiftY})` : undefined),
    },
    children: children,
  };

  if (Component === 'h1') {
    return (
      <h1 {...containerProps}>
        <span {...textProps} />
      </h1>
    );
  }

  if (Component === 'h2') {
    return (
      <h2 {...containerProps}>
        <span {...textProps} />
      </h2>
    );
  }

  if (Component === 'h3') {
    return (
      <h3 {...containerProps}>
        <span {...textProps} />
      </h3>
    );
  }

  return (
    <div {...containerProps}>
      <span {...textProps} />
    </div>
  );
};

export default AutoFitText;

/**
 * Ejemplo de uso:
 * 
 * <div style={{ width: 240 }}>
 *   <AutoFitText maxFont={40} minFont={12}>
 *     Este es un texto largo que debe adaptarse
 *   </AutoFitText>
 * </div>
 */
